/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import {
  formatDate,
  formatNumber,
  redirectToExtern,
  intlShape,
} from "lcm-iot-commons";
import { Component } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { serviceSubscriptionShape } from "../../shapes/serviceSubscriptionsShape";

type Props = {
  intl: intlShape;
  subscription: serviceSubscriptionShape;
  resellerView: boolean;
};

class ServiceSubscriptionItem extends Component<Props> {
  constructor(props: Props) {
    super(props);
    /* istanbul ignore next */
    this.handleOnClick = this.handleOnClick.bind(this);
  }

  getIconForApp(appIconName: $TSFixMe) {
    if (
      [
        "health",
        "library",
        "analytics",
        "value",
        "netilion",
        "inventory",
      ].includes(appIconName)
    ) {
      return `icon lcm-iot-icon-${appIconName}-rounded`;
    }
    if (appIconName === "fermentation_monitor") {
      return "icon icon3-eh-netilion-fermentation icon-style-fermentation";
    }
    return "icon lcm-iot-icon-circle";
  }

  getSubscriptionStatusLabel(subscription: $TSFixMe) {
    switch (subscription.status) {
      case "confirmed":
        return "subscription_details.active";
      case "non_renewing":
        return "subscription_details.non_renewing";
      case "open":
        return "subscription_details.open";
      case "cancelled":
        return "subscription_details.cancelled";
      case "payment_required":
        return "subscription_details.payment_required";
      case "in_trial":
        if (subscription.cancelledAt) {
          return "subscription_details.non_renewing";
        }
        return "subscription_details.in_trial";
      default:
        return "subscription_details.active";
    }
  }

  redirectToGeneralUrl(subscription: serviceSubscriptionShape) {
    const { resellerView } = this.props;
    const appSpecificUrl =
      subscription.appIconName === "fermentation_monitor"
        ? "fermentation"
        : subscription.appIconName;
    // normal case
    let url = `/app/${appSpecificUrl}/subscription`;
    if (resellerView) {
      // reseller
      url = `/app/${appSpecificUrl}/reseller/subscriptions/${subscription.id}`;
    } else if (!subscription.usable) {
      url = `/app/${appSpecificUrl}/subscriptions/${subscription.id}`;
    }
    redirectToExtern(url);
  }

  handleOnClick() {
    const { subscription } = this.props;
    this.redirectToGeneralUrl(subscription);
  }

  getAssetQuotaDisplay(
    subscription: serviceSubscriptionShape,
    intl: intlShape,
  ) {
    let assetQuota = formatNumber(subscription.assetQuota);
    if (assetQuota === "-1") {
      assetQuota = intl.formatMessage({ id: "label.unlimited" });
    }
    return assetQuota;
  }

  renderIntervalAndTag(assets: $TSFixMe, interval: $TSFixMe) {
    if (assets?.[0]?.instrumentations?.[0]?.tag) {
      return (
        <div>
          <FormattedMessage id="label.tag" />:
          {assets[0].instrumentations[0].tag},{interval}{" "}
          <FormattedMessage id="label.minutes" />{" "}
          <FormattedMessage id="label.interval" />
        </div>
      );
    }
    return null;
  }

  renderAssetAndStorageQuota(
    subscription: serviceSubscriptionShape,
    assetQuota: $TSFixMe,
  ) {
    if (subscription.assetQuota) {
      return (
        <div>
          <FormattedMessage
            id="label.subscription_service_quota"
            values={{ assetQuota }}
          />
        </div>
      );
    }
    return null;
  }

  renderCurrentTermDetails(
    subscription: serviceSubscriptionShape,
    intl: intlShape,
  ) {
    const { pricingModel, status, billedUntil, trialEndDate } = subscription;
    const subscriptionStatus = (
      <span>
        <FormattedMessage id="label.status" />:{" "}
        <FormattedMessage id={this.getSubscriptionStatusLabel(subscription)} />{" "}
      </span>
    );

    if (pricingModel !== "free") {
      let statusDetails;
      if (status === "in_trial") {
        statusDetails = (
          <FormattedMessage
            id="label.ending_on"
            values={{ date: formatDate(trialEndDate, intl) }}
          />
        );
      } else {
        statusDetails =
          status !== "payment_required" ? (
            <FormattedMessage
              id={
                status === "confirmed" ? "label.renewing_on" : "label.ending_on"
              }
              values={{ date: formatDate(billedUntil, intl) }}
            />
          ) : null;
      }

      return (
        <div>
          {subscriptionStatus}
          {statusDetails}
        </div>
      );
    }
    return (
      <div>
        {subscriptionStatus}
        <FormattedMessage id="label.free_subscription" />
      </div>
    );
  }

  renderResellerLabel(
    resellerView: boolean,
    resellerLabel: string | undefined,
  ) {
    if (resellerView && resellerLabel) {
      return ` - ${resellerLabel}`;
    }
    return null;
  }

  renderUsableElement(resellerView: boolean, usable: boolean, intl: intlShape) {
    if (!resellerView && usable === false) {
      return ` - ${intl.formatMessage({ id: "label.can_read_subscription" })}`;
    }
    return null;
  }

  render() {
    const { subscription, intl, resellerView } = this.props;
    const {
      applicationName,
      subscriptionName,
      appIconName,
      interval,
      assets,
      resellerLabel,
      usable,
    } = subscription;
    const externalRef = `ID: ${subscription.id}`;
    const assetQuota = this.getAssetQuotaDisplay(subscription, intl);
    const iconClassname = this.getIconForApp(appIconName);
    const intervalAndTag = this.renderIntervalAndTag(assets, interval);
    const assetAndStorageQuota = this.renderAssetAndStorageQuota(
      subscription,
      assetQuota,
    );
    const currentTermDetails = this.renderCurrentTermDetails(
      subscription,
      intl,
    );
    const resellerLabelElement = this.renderResellerLabel(
      resellerView,
      resellerLabel,
    );
    const usableElement = this.renderUsableElement(resellerView, usable, intl);
    const isCancelled = subscription.status?.toLowerCase() === "cancelled";
    const isOpen = subscription.status?.toLowerCase() === "open";

    return (
      <li
        data-testid={`item-${applicationName}`}
        className={
          isCancelled || isOpen ? "list-item disabled" : "list-item clickable"
        }
        onClick={isCancelled || isOpen ? () => {} : this.handleOnClick}
      >
        <div className="list-item-image">
          <span className={iconClassname} id="application-icon" />
        </div>
        <div className="list-item-content">
          <div className="list-item-header">
            {applicationName}
            {" - "}
            {subscriptionName ||
              intl.formatMessage({ id: "subscription_details.open" })}
            {resellerLabelElement}
            {usableElement}
          </div>
          <div>
            {assetAndStorageQuota}
            {intervalAndTag}
            {currentTermDetails}
            {externalRef}
          </div>
        </div>
      </li>
    );
  }
}

export default injectIntl(ServiceSubscriptionItem);
